import { useState } from "react";
import {
  Box,
  Text,
  Button,
  ProgressBar,
  Spinner,
} from "@primer/react";
import {
  HistoryIcon,
  CreditCardIcon,
  PlusIcon,
} from "@primer/octicons-react";
import { DashboardLayout } from "../components/DashboardLayout";
import { formatDistanceToNow } from "date-fns";
import { useWallet } from "../hooks/useWallet";
import { WalletTopupModal } from "../components/WalletTopupModal";
import { TransactionHistory } from "../components/TransactionHistory";
import { Toast } from "../components/Toast";


export default function Usage() {
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isTopupModalOpen, setIsTopupModalOpen] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  const { walletData, loading: walletLoading, refresh: refreshWallet } = useWallet();

  const usageData = {
    freeLimit: walletData?.free_usage_limit || 1000,
    used: walletData?.free_usage || 0,
    tokens: {
      available: walletData?.available_tokens || 0,
      used: walletData?.token_usage || 0,
    },
    wallet: {
      balance: walletData?.amount || 0,
      lastUpdated: walletData?.updated_at || new Date(),
    },
  };

  const calculatePercentage = (used: number, total: number) => {
    if (total === 0) return 0;
    return (used / total) * 100;
  };

  return (
    <DashboardLayout data-oid="xbezqdg">
      <Box className="app-page" data-oid=":59:8xu">
        <Box className="app-page-header">
          <Box className="app-page-heading">
            <Text as="h1" data-oid="_exdh.0">Usage & Billing</Text>
            <p className="app-page-subtitle">A clear view of your free generations, token balance, and billing activity.</p>
          </Box>
        </Box>

        {walletLoading ? (
          <Box
            className="usage-grid"
            sx={{ display: "flex", justifyContent: "center", p: 4 }}
            data-oid="whd:8qi"
          >
            <Spinner size="large" data-oid="gjg6gbh" />
          </Box>
        ) : (
          <Box
            className="usage-grid"
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr",
              gap: 4,
              maxWidth: "1200px",
            }}
            data-oid="nxp070u"
          >
            {/* Wallet Section */}
            <Box data-oid="1h8extr">
              <Box
                className="app-panel wallet-card usage-wallet-card"
                sx={{
                  p: 4,
                  border: "1px solid",
                  borderColor: "border.default",
                  borderRadius: 2,
                  bg: "canvas.subtle",
                }}
                data-oid="tg34ia9"
              >
                <Box
                  className="wallet-card-header"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    mb: 3,
                  }}
                  data-oid="npk96lo"
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <CreditCardIcon size={16} data-oid="xcc_.o1" />
                    <Text
                      sx={{ fontSize: 2, fontWeight: "bold" }}
                      data-oid="ub8.ah4"
                    >
                      Wallet
                    </Text>
                  </Box>
                  <Button
                    onClick={() => setIsHistoryModalOpen(true)}
                    variant="invisible"
                    leadingVisual={HistoryIcon}
                    className="usage-transactions-link"
                  >
                    Transactions
                  </Button>
                </Box>

                <Box sx={{ mb: 4 }} data-oid="nbfqazo">
                  <Text
                    sx={{
                      fontSize: 4,
                      fontWeight: "bold",
                      display: "block",
                      mb: 2,
                    }}
                    data-oid="ls11s9f"
                  >
                    ${usageData.wallet.balance.toFixed(2)}
                  </Text>
                  <Box
                    sx={{
                      pt: 2,
                      borderTop: "1px solid",
                      borderColor: "border.muted",
                    }}
                    data-oid="nzi6zsj"
                  >
                    <Text
                      sx={{
                        fontSize: "12px",
                        color: "fg.muted",
                      }}
                      data-oid="l14c.ak"
                    >
                      Last updated{" "}
                      {formatDistanceToNow(usageData.wallet.lastUpdated)} ago
                    </Text>
                  </Box>
                </Box>

                <Box
                  className="usage-wallet-actions"
                  sx={{ display: "flex", gap: 2, flexDirection: "column" }}
                  data-oid="i0::wze"
                >
                  <Button
                    onClick={() => setIsTopupModalOpen(true)}
                    variant="primary"
                    leadingVisual={PlusIcon}
                    className="app-primary-button usage-topup-button"
                    sx={{ width: "100%" }}
                  >
                    Top Up Wallet
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Usage Section */}
            <Box data-oid="lc80-ah">
              {/* Free Usage Section */}
              <Box
                className="app-panel usage-meter-card usage-meter-card-free"
                sx={{
                  p: 4,
                  border: "1px solid",
                  borderColor: "border.default",
                  borderRadius: 2,
                  mb: 4,
                }}
                data-oid="q2wshgq"
              >
                <Text
                  sx={{ fontSize: 2, fontWeight: "bold", mb: 3 }}
                  data-oid="8b8pxbv"
                >
                  Free Usage
                </Text>

                <Box sx={{ mb: 2 }} data-oid="xldf1le">
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                    data-oid="-11q-1q"
                  >
                    <Text data-oid="1-jkvs_">
                      Remaining {usageData.freeLimit - usageData.used} of{" "}
                      {usageData.freeLimit}
                    </Text>
                    <Text data-oid="mft.-k0">
                      {calculatePercentage(
                        usageData.freeLimit - usageData.used,
                        usageData.freeLimit,
                      ).toFixed(1)}
                      %
                    </Text>
                  </Box>
                  <ProgressBar
                    progress={calculatePercentage(
                      usageData.freeLimit - usageData.used,
                      usageData.freeLimit,
                    )}
                    sx={{
                      bg: "border.default",
                      "> div": {
                        bg: "#6f3ff5",
                      },
                    }}
                    data-oid="u196ss."
                  />

                  <Text
                    sx={{
                      fontSize: 1,
                      color: "fg.muted",
                      mt: 2,
                    }}
                    data-oid="c7wuf:a"
                  >
                    {usageData.used} usages consumed
                  </Text>
                </Box>
              </Box>

              {/* Tokens Section */}
              <Box
                className="app-panel usage-meter-card usage-meter-card-tokens"
                sx={{
                  p: 4,
                  border: "1px solid",
                  borderColor: "border.default",
                  borderRadius: 2,
                }}
                data-oid="bmzr6hr"
              >
                <Text
                  sx={{ fontSize: 2, fontWeight: "bold", mb: 3 }}
                  data-oid="09r:pyq"
                >
                  Tokens
                </Text>

                <Box sx={{ mb: 2 }} data-oid="oqrr59l">
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                    data-oid="sf.s29q"
                  >
                    <Text data-oid=":gb04o:">
                      Remaining {usageData.tokens.available} of{" "}
                      {usageData.tokens.used + usageData.tokens.available}
                    </Text>
                    <Text data-oid="zwfyim8">
                      {calculatePercentage(
                        usageData.tokens.available,
                        usageData.tokens.used + usageData.tokens.available,
                      ).toFixed(1)}
                      %
                    </Text>
                  </Box>
                  <ProgressBar
                    progress={calculatePercentage(
                      usageData.tokens.available,
                      usageData.tokens.used + usageData.tokens.available,
                    )}
                    sx={{
                      bg: "border.default",
                      "> div": {
                        bg: "accent.emphasis",
                      },
                    }}
                    data-oid="c4k1u_l"
                  />

                  <Text
                    sx={{
                      fontSize: 1,
                      color: "fg.muted",
                      mt: 2,
                    }}
                    data-oid="2revqz1"
                  >
                    {usageData.tokens.used} tokens consumed
                  </Text>
                  <Box className="usage-token-note">
                    <strong>1 token = 1 PDF generation</strong>
                    <span>Only successful API calls consume a token.</span>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* Transaction History Modal */}
        <TransactionHistory 
          isOpen={isHistoryModalOpen} 
          onClose={() => setIsHistoryModalOpen(false)} 
        />

        {/* Wallet Top-up Modal */}
        <WalletTopupModal
          isOpen={isTopupModalOpen}
          onDismiss={() => setIsTopupModalOpen(false)}
          onSuccess={async () => {
            await refreshWallet();
            setShowSuccessToast(true);
            setTimeout(() => setShowSuccessToast(false), 5000);
          }}
        />

        {/* Success Toast */}
        {showSuccessToast && (
          <Toast message="Wallet top-up successful! Your tokens have been added." />
        )}
      </Box>
    </DashboardLayout>
  );
}
